import useEmailEditor from '@hooks/useEmailEditor';
import useProperty from '@hooks/useProperty';
import useSelectedElement from '@hooks/useSelectedElement';
import { HorizontalRuleRounded } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactEditor, useSlate } from 'slate-react';

function ColumnResizer({
    element,
    path,
    isDragging,
    setIsDragging,
    setWidth,
    rtl = false,
}) {
    const { t } = useTranslation();
    const { iframeRef } = useEmailEditor();
    const { getSelectedElement } = useSelectedElement();
    const [, selectedElement] = getSelectedElement();
    const editor = useSlate();
    const { setProperty } = useProperty('style.width', 'attributes', path);
    const elementTarget = useRef(null);

    const iframeRect = iframeRef.current.getBoundingClientRect();

    const elementStyle = elementTarget?.current?.style || {};

    useEffect(() => {
        elementTarget.current = ReactEditor.toDOMNode(editor, element);
    }, []);

    if (selectedElement) return null;

    const rowRect =
        elementTarget.current?.parentElement?.getBoundingClientRect();

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;

        const elementRect = elementTarget.current.getBoundingClientRect();

        const rtlClientX = iframeRect.right - e.clientX;
        const rtlPosition = rtlClientX - (iframeRect.right - elementRect.right);

        const ltrPosition = e.clientX - iframeRect.left - elementRect.left;
        const position = rtl ? rtlPosition : ltrPosition;

        const calculatedWidth = Math.max(
            1,
            Math.min(99, Math.round((position / rowRect.width) * 100)),
        );

        setWidth(calculatedWidth + '%');
        elementStyle.width = calculatedWidth + '%';
    };

    const handleFinish = () => {
        if (!isDragging) return;
        const { width } = elementStyle;
        setProperty(width ? width : null);
        setIsDragging(false);
        setWidth(null);
    };

    return (
        <Tooltip title={elementStyle.width || t('Auto')} followCursor>
            <Box
                onMouseMove={handleMouseMove}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={handleFinish}
                onMouseLeave={handleFinish}
                onDoubleClick={() => setProperty(null)}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: isDragging ? 100 : 8,
                    height: '100%',
                    transform: 'translateX(50%)',
                    cursor: 'e-resize',
                    pointerEvents: 'auto',
                    overflow: 'hidden',
                    opacity: 0,
                    '&:hover': {
                        opacity: 1,
                    },
                }}
            >
                <Box
                    sx={{
                        width: 2,
                        height: '100%',
                        marginX: 'auto',
                        borderRadius: 99,
                        backgroundColor: theme => theme.palette.primary.main,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: 99,
                            backgroundColor: theme =>
                                theme.palette.secondary.main,
                            width: 8,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <HorizontalRuleRounded
                            sx={{
                                transform: 'rotate(90deg)',
                                color: theme =>
                                    theme.palette.primary.contrastText,
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Tooltip>
    );
}

export default ColumnResizer;
